// src/screens/AdminDashboardScreen.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, ActivityIndicator,
  StyleSheet, SafeAreaView, ScrollView, Switch,
} from 'react-native';

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, query, where, getDocs, doc, getDoc, Timestamp } from 'firebase/firestore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useAdminAuth } from '../hooks/useAdminAuth';
import useCritsEnabled from '../hooks/useCritsEnabled';

// --- Helpers ---
const usd = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100);
const num = (n) => new Intl.NumberFormat('en-US').format(n || 0);

const COLORS = { 
  GOLD: '#FCD000', 
  BG: '#0b0b0b', 
  CARD: '#101010', 
  TEXT: '#EDEDED', 
  DIM: '#A0A0A0', 
  BORDER: '#1a1a1a', 
  SUCCESS: '#2ECC71', 
  DANGER: '#E74C3C', 
  BLUE: '#3498DB' 
};

/*
 * --- Data Hooks ---
 */

// Fetches admin KPIs via cloud function with Firestore fallback
function useAdminKpis(isAdmin, isLoading) {
  const [kpis, setKpis] = useState({
    totalUsers: 0, 
    newUsers24h: 0, 
    newUsers7d: 0, 
    newUsers30d: 0,
    liveEvents: 0, 
    eventsToday: 0, 
    totalEvents: 0,
    grossRevenue: 0, 
    netRevenue: 0, 
    totalPayouts: 0, 
    activePot: 0,
    pendingWithdrawals: 0, 
    loading: true,
  });

  useEffect(() => {
    if (isLoading || !isAdmin) {
      setKpis(p => ({ ...p, loading: false }));
      return;
    }

    const fetchKpis = async () => {
      try {
        const getKpis = httpsCallable(getFunctions(), 'getAdminDashboardKpis');
        const result = await getKpis();
        setKpis({ ...result.data, loading: false });
      } catch (error) {
        console.log('[AdminDashboard] Cloud function failed, trying Firestore fallback:', error.message);
        await fetchKpisFromFirestore();
      }
    };

    const fetchKpisFromFirestore = async () => {
      try {
        const db = getFirestore();

        // --- Users ---
        const usersSnap = await getDocs(collection(db, 'users'));
        const totalUsers = usersSnap.size;

        const now = Date.now();
        const ms24h = 24 * 60 * 60 * 1000;
        const ms7d = 7 * ms24h;
        const ms30d = 30 * ms24h;
        let newUsers24h = 0;
        let newUsers7d = 0;
        let newUsers30d = 0;

        usersSnap.docs.forEach(d => {
          const data = d.data();
          const created = data.createdAt?.toDate ? data.createdAt.toDate().getTime()
            : data.createdAt?.seconds ? data.createdAt.seconds * 1000
            : (typeof data.createdAt === 'string' ? new Date(data.createdAt).getTime() : 0);
          if (created) {
            const age = now - created;
            if (age < ms24h) newUsers24h++;
            if (age < ms7d) newUsers7d++;
            if (age < ms30d) newUsers30d++;
          }
        });

        // --- Events (tournaments + matches) ---
        const [tourneysSnap, matchesSnap] = await Promise.all([
          getDocs(collection(db, 'tournaments')),
          getDocs(collection(db, 'matches')),
        ]);

        const totalEvents = tourneysSnap.size + matchesSnap.size;
        let liveEvents = 0;
        let eventsToday = 0;
        let grossRevenue = 0;
        let netRevenue = 0;
        let totalPayouts = 0;

        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayMs = todayStart.getTime();

        const processEvents = (snap) => {
          snap.docs.forEach(d => {
            const data = d.data();
            if (data.status === 'live') liveEvents++;

            // Count events created today
            const created = data.createdAt?.toDate ? data.createdAt.toDate().getTime()
              : data.createdAt?.seconds ? data.createdAt.seconds * 1000 : 0;
            if (created >= todayMs) eventsToday++;

            // Financial: only completed/finalized events contribute
            if (['completed', 'finalized'].includes(data.status)) {
              const entryFee = data.entryFeeCents || 0;
              const entrants = data.entrantsCount || data.maxEntrants || 0;
              const gross = entryFee * entrants;
              const platformFee = data.platformFeeCents || Math.floor(gross * 0.10);
              const pool = data.prizePoolCents || (gross - platformFee);

              grossRevenue += gross;
              netRevenue += platformFee;
              totalPayouts += pool;
            }
          });
        };

        processEvents(tourneysSnap);
        processEvents(matchesSnap);

        // --- Active escrow ---
        // CritCash doesn't have a separate held-funds bucket on user docs —
        // entry fees debit users.balanceCents immediately at join time, no
        // escrow layer. Sum of "money locked in live events" is approximated
        // by tournaments.financials.totalEntryFees on non-terminal events
        // (same source the reconciliation function uses for in-flight pool
        // tracking). The 2026-05-25 audit confirmed the prior
        // `wallets/heldCents` read returned 0 docs and a non-existent field.
        let activePot = 0;
        try {
          tourneysSnap.docs.forEach(d => {
            const data = d.data();
            if (!['completed', 'cancelled', 'draft'].includes(data.status)) {
              activePot += data.financials?.totalEntryFees || 0;
            }
          });
        } catch (e) {
          console.log('[AdminDashboard] Could not compute active escrow:', e.message);
        }

        // --- Pending withdrawals ---
        // Canonical withdrawals collection (not the legacy `withdrawal_requests`
        // — that returned 0 docs in the 2026-05-25 audit). Includes both
        // pre-execution states: 'pending' (immediate flow) and 'pending_hold'
        // (24h hold queue from Task #5).
        let pendingWithdrawals = 0;
        try {
          const wdSnap = await getDocs(
            query(collection(db, 'withdrawals'), where('status', 'in', ['pending', 'pending_hold']))
          );
          pendingWithdrawals = wdSnap.size;
        } catch (e) {
          console.log('[AdminDashboard] Could not read withdrawals:', e.message);
        }

        setKpis({
          totalUsers,
          newUsers24h,
          newUsers7d,
          newUsers30d,
          liveEvents,
          eventsToday,
          totalEvents,
          grossRevenue,
          netRevenue,
          totalPayouts,
          activePot,
          pendingWithdrawals,
          loading: false,
        });

      } catch (fsErr) {
        console.error('[AdminDashboard] Firestore fallback also failed:', fsErr);
        setKpis(p => ({ ...p, loading: false }));
      }
    };

    fetchKpis();

  }, [isAdmin, isLoading]);

  return kpis;
}

// Hook to get Stripe status for dashboard indicator
function useStripeStatus(isAdmin, isLoading) {
  const [stripeStatus, setStripeStatus] = useState({
    enabled: false,
    testMode: true,
    loading: true,
  });

  useEffect(() => {
    if (isLoading || !isAdmin) {
      setStripeStatus(p => ({ ...p, loading: false }));
      return;
    }

    const fetchStatus = async () => {
      try {
        const getConfig = httpsCallable(getFunctions(), 'getPaymentPublicConfig');
        const result = await getConfig();
        setStripeStatus({
          enabled: result.data.paymentsEnabled,
          testMode: result.data.testMode,
          depositsEnabled: result.data.deposits?.enabled,
          withdrawalsEnabled: result.data.withdrawals?.enabled,
          loading: false,
        });
      } catch (error) {
        console.log('[AdminDashboard] Payment config CF failed, trying Firestore:', error.message);
        // Firestore fallback: read config/stripe directly
        try {
          const db = getFirestore();
          const configDoc = await getDoc(doc(db, 'config', 'stripe'));
          if (configDoc.exists()) {
            const data = configDoc.data();
            setStripeStatus({
              enabled: data.paymentsEnabled ?? false,
              testMode: data.testMode ?? true,
              depositsEnabled: data.deposits?.enabled ?? false,
              withdrawalsEnabled: data.withdrawals?.enabled ?? false,
              loading: false,
            });
          } else {
            setStripeStatus(p => ({ ...p, loading: false }));
          }
        } catch (fsErr) {
          console.log('[AdminDashboard] Firestore config fallback failed:', fsErr.message);
          setStripeStatus(p => ({ ...p, loading: false }));
        }
      }
    };

    fetchStatus();
  }, [isAdmin, isLoading]);

  return stripeStatus;
}


// Hook to get event creation config
function useEventConfig(isAdmin, isLoading) {
  const [eventConfig, setEventConfig] = useState({
    adminOnlyEventCreation: true,
    hideHeats: false,
    hideTournaments: false,
    loading: true,
    toggling: false,
    togglingMode: null, // 'heats' | 'tournaments' while a mode toggle is in flight
  });

  const fetchConfig = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const getConfig = httpsCallable(getFunctions(), 'getEventConfig');
      const result = await getConfig();
      setEventConfig(prev => ({
        ...prev,
        adminOnlyEventCreation: result.data.adminOnlyEventCreation ?? true,
        hideHeats: result.data.hideHeats ?? false,
        hideTournaments: result.data.hideTournaments ?? false,
        loading: false,
      }));
    } catch (error) {
      console.error('[AdminDashboard] Failed to fetch event config:', error.message);
      setEventConfig(prev => ({ ...prev, loading: false }));
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isLoading || !isAdmin) {
      setEventConfig(prev => ({ ...prev, loading: false }));
      return;
    }
    fetchConfig();
  }, [isAdmin, isLoading, fetchConfig]);

  const toggleAdminOnly = async (enabled) => {
    setEventConfig(prev => ({ ...prev, toggling: true }));
    try {
      const toggle = httpsCallable(getFunctions(), 'toggleAdminOnlyEvents');
      const result = await toggle({ enabled });
      setEventConfig(prev => ({
        ...prev,
        adminOnlyEventCreation: result.data.adminOnlyEventCreation,
        toggling: false,
      }));
    } catch (error) {
      console.error('[AdminDashboard] Failed to toggle event creation:', error.message);
      setEventConfig(prev => ({ ...prev, toggling: false }));
    }
  };

  const toggleHideMode = async (mode, enabled) => {
    setEventConfig(prev => ({ ...prev, togglingMode: mode }));
    try {
      const toggle = httpsCallable(getFunctions(), 'toggleHideEventMode');
      const result = await toggle({ mode, enabled });
      setEventConfig(prev => ({
        ...prev,
        [mode === 'heats' ? 'hideHeats' : 'hideTournaments']: result.data.enabled,
        togglingMode: null,
      }));
    } catch (error) {
      console.error(`[AdminDashboard] Failed to toggle ${mode}:`, error.message);
      setEventConfig(prev => ({ ...prev, togglingMode: null }));
    }
  };

  return { eventConfig, toggleAdminOnly, toggleHideMode };
}

// Fetches the current season state (preseason / active + 2-month clock).
function useSeason(isAdmin, isLoading) {
  const [season, setSeason] = useState({ loading: true });
  const refreshSeason = useCallback(async () => {
    try {
      const getSeason = httpsCallable(getFunctions(), 'getSeason');
      const res = await getSeason({});
      setSeason({ loading: false, ...(res.data || {}) });
    } catch (e) {
      setSeason({ loading: false, error: e.message });
    }
  }, []);
  useEffect(() => {
    if (isLoading || !isAdmin) { setSeason(p => ({ ...p, loading: false })); return; }
    refreshSeason();
  }, [isAdmin, isLoading, refreshSeason]);
  return { season, refreshSeason };
}

export default function AdminDashboardScreen({ navigation }) {
  const { user, isAdmin, isLoading } = useAdminAuth();
  const kpis = useAdminKpis(isAdmin, isLoading);
  const stripeStatus = useStripeStatus(isAdmin, isLoading);
  const { eventConfig, toggleAdminOnly, toggleHideMode } = useEventConfig(isAdmin, isLoading);
  const { season, refreshSeason } = useSeason(isAdmin, isLoading);
  const [seasonBusy, setSeasonBusy] = useState(false);
  // Economy flag (evergreen step 2.3): settings/economy.critsEnabled. With it
  // off, the irreversible Start Season 1 button is hidden and the card shows
  // the flag state. Called before the early returns (rules of hooks).
  const critsEnabled = useCritsEnabled();

  if (isLoading || !user) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.centerWrap}>
          <ActivityIndicator color={COLORS.GOLD} />
        </View>
      </SafeAreaView>
    );
  }

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.centerWrap}>
          <Text style={styles.deniedText}>Access Denied</Text>
          <TouchableOpacity onPress={() => navigation.navigate('Lobby')}>
            <Text style={styles.backLink}>Go to Lobby</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Helper to get banner icon
  const getBannerIcon = () => {
    if (!stripeStatus.enabled) return '[OFF]';
    if (stripeStatus.testMode) return '[TEST]';
    return '[LIVE]';
  };

  // One-time, irreversible launch of Season 1.
  const handleStartSeason = async () => {
    if (!critsEnabled) return;
    const ok = typeof window !== 'undefined'
      ? window.confirm('Start Season 1?\n\nThis is PERMANENT and CANNOT be undone. It begins full launch mode — every player\'s Crits reset to their tier grant every 2 months, rolling on the 1st.')
      : true;
    if (!ok) return;
    setSeasonBusy(true);
    try {
      const start = httpsCallable(getFunctions(), 'startSeasonOne');
      const res = await start({});
      if (typeof window !== 'undefined') window.alert(res.data?.message || 'Season 1 started.');
      await refreshSeason();
    } catch (e) {
      const msg = (e?.message || String(e)).replace(/^season-already-started:/, '');
      if (typeof window !== 'undefined') window.alert('Could not start season: ' + msg);
    } finally {
      setSeasonBusy(false);
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Admin Command Center</Text>
          <Text style={styles.subtitle}>Platform Management & Analytics</Text>
        </View>
        
        {/* Stripe Status Banner */}
        {!stripeStatus.loading && (
          <TouchableOpacity 
            style={[
              styles.stripeBanner,
              stripeStatus.enabled 
                ? (stripeStatus.testMode ? styles.stripeBannerTest : styles.stripeBannerLive)
                : styles.stripeBannerDisabled
            ]}
            onPress={() => navigation.navigate('PaymentSettings')}
          >
            <Text style={styles.stripeBannerIcon}>{getBannerIcon()}</Text>
            <View style={styles.stripeBannerText}>
              <Text style={styles.stripeBannerTitle}>
                Payments: {stripeStatus.enabled 
                  ? (stripeStatus.testMode ? 'TEST MODE' : 'LIVE') 
                  : 'DISABLED'}
              </Text>
              <Text style={styles.stripeBannerSub}>
                {stripeStatus.enabled 
                  ? 'Deposits: ' + (stripeStatus.depositsEnabled ? 'ON' : 'OFF') + ' | Withdrawals: ' + (stripeStatus.withdrawalsEnabled ? 'ON' : 'OFF')
                  : 'Tap to configure'}
              </Text>
            </View>
            <Text style={styles.stripeBannerArrow}>&rarr;</Text>
          </TouchableOpacity>
        )}
        
        {/* Event Creation Toggle */}
        {!eventConfig.loading && (
          <View style={styles.eventToggleBanner}>
            <View style={styles.eventToggleText}>
              <Text style={styles.eventToggleTitle}>
                {eventConfig.adminOnlyEventCreation ? 'Event Creation: Admin Only' : 'Event Creation: Open'}
              </Text>
              <Text style={styles.eventToggleSub}>
                {eventConfig.adminOnlyEventCreation
                  ? 'Only admins can create events'
                  : 'All users can create events'}
              </Text>
            </View>
            {eventConfig.toggling ? (
              <ActivityIndicator color={COLORS.GOLD} size="small" />
            ) : (
              <Switch
                value={eventConfig.adminOnlyEventCreation}
                onValueChange={(newVal) => toggleAdminOnly(newVal)}
                trackColor={{ false: '#333', true: '#E74C3C' }}
                thumbColor={eventConfig.adminOnlyEventCreation ? '#fff' : '#888'}
              />
            )}
          </View>
        )}

        {/* Heats Visibility Toggle */}
        {!eventConfig.loading && (
          <View style={styles.eventToggleBanner}>
            <View style={styles.eventToggleText}>
              <Text style={styles.eventToggleTitle}>
                {eventConfig.hideHeats ? 'Heats: Hidden' : 'Heats: Public'}
              </Text>
              <Text style={styles.eventToggleSub}>
                {eventConfig.hideHeats
                  ? 'Users can\'t create heats or see them in the lobby — admins still can'
                  : 'All users can create and see heat events'}
              </Text>
            </View>
            {eventConfig.togglingMode === 'heats' ? (
              <ActivityIndicator color={COLORS.GOLD} size="small" />
            ) : (
              <Switch
                value={eventConfig.hideHeats}
                onValueChange={(newVal) => toggleHideMode('heats', newVal)}
                trackColor={{ false: '#333', true: '#E74C3C' }}
                thumbColor={eventConfig.hideHeats ? '#fff' : '#888'}
              />
            )}
          </View>
        )}

        {/* Tournaments Visibility Toggle */}
        {!eventConfig.loading && (
          <View style={styles.eventToggleBanner}>
            <View style={styles.eventToggleText}>
              <Text style={styles.eventToggleTitle}>
                {eventConfig.hideTournaments ? 'Tournaments: Hidden' : 'Tournaments: Public'}
              </Text>
              <Text style={styles.eventToggleSub}>
                {eventConfig.hideTournaments
                  ? 'Users can\'t create tournaments or see them in the lobby — admins still can'
                  : 'All users can create and see tournament events'}
              </Text>
            </View>
            {eventConfig.togglingMode === 'tournaments' ? (
              <ActivityIndicator color={COLORS.GOLD} size="small" />
            ) : (
              <Switch
                value={eventConfig.hideTournaments}
                onValueChange={(newVal) => toggleHideMode('tournaments', newVal)}
                trackColor={{ false: '#333', true: '#E74C3C' }}
                thumbColor={eventConfig.hideTournaments ? '#fff' : '#888'}
              />
            )}
          </View>
        )}

        <Section title="Season Control">
          {season.loading ? (
            <ActivityIndicator color={COLORS.GOLD} />
          ) : season.status === 'active' ? (
            <View>
              <Text style={styles.seasonStatus}>Season {season.seasonNumber} — LIVE</Text>
              <Text style={styles.seasonSub}>
                {!critsEnabled
                  ? 'Crit economy is OFF (settings/economy.critsEnabled = false). Season clock idle.'
                  : season.endsAt
                  ? `Next Crit reset: ${new Date(season.endsAt).toLocaleDateString()} (seasons roll on the 1st, every 2 months)`
                  : 'Crits reset every 2 months, on the 1st'}
              </Text>
            </View>
          ) : (
            <View>
              <Text style={styles.seasonStatus}>Pre-Season</Text>
              <Text style={styles.seasonSub}>
                {critsEnabled
                  ? 'Crits never reset right now. Starting Season 1 begins full launch mode — Crits reset every 2 months, on the 1st — and CANNOT be undone.'
                  : 'Crit economy is OFF (settings/economy.critsEnabled = false). Season clock idle.'}
              </Text>
              {critsEnabled ? (
                <View style={{ marginTop: 10 }}>
                  <DashBtn
                    label={seasonBusy ? 'Starting…' : 'Start Season 1'}
                    onPress={seasonBusy ? () => {} : handleStartSeason}
                    kind="primary"
                  />
                </View>
              ) : null}
            </View>
          )}
        </Section>

        <Section title="Quick Actions">
          <View style={styles.quickGrid}>
            <DashBtn
              label="Match Disputes"
              onPress={() => navigation.navigate('AdminDisputes')}
              kind="primary"
            />
            <DashBtn
              label="Manage Events"
              onPress={() => navigation.navigate('ManageEvents')}
              kind="primary"
            />
            <DashBtn
              label="Branded Events"
              onPress={() => navigation.navigate('AdminBrandedEvents')}
              kind="primary"
            />
            <DashBtn 
              label="Manage Users" 
              onPress={() => navigation.navigate('ManageUsers')} 
              kind="primary" 
            />
            <DashBtn 
              label="Financials" 
              onPress={() => navigation.navigate('Financials')} 
              kind="primary" 
            />
            <DashBtn
              label="Payment Settings"
              onPress={() => navigation.navigate('PaymentSettings')}
              kind="primary"
            />
            <DashBtn
              label="Comp Overview"
              onPress={() => navigation.navigate('AdminCompOverview')}
              kind="primary"
            />
            <DashBtn
              label="Game Demand"
              onPress={() => navigation.navigate('AdminGameDemand')}
              kind="primary"
            />
            <DashBtn 
              label="Evidence Viewer" 
              onPress={() => navigation.navigate('EventEvidence')} 
              kind="secondary" 
            />
            <DashBtn
              label="Game Availability"
              onPress={() => navigation.navigate('GameAvailability')} 
              kind="secondary" 
            />
            <DashBtn
              label="Admin Tools"
              onPress={() => navigation.navigate('AdminTools')}
              kind="secondary"
            />
            <DashBtn
              label="Developer Reviews"
              onPress={() => navigation.navigate('DeveloperDashboard')}
              kind="secondary"
            />
            <DashBtn
              label="Streamer Outreach"
              onPress={() => navigation.navigate('AdminStreamerOutreach')}
              kind="secondary"
            />
            <DashBtn
              label="AML Review"
              onPress={() => navigation.navigate('AdminAmlReview')}
              kind="secondary"
            />
            <DashBtn
              label="Reconciliation"
              onPress={() => navigation.navigate('AdminReconciliation')}
              kind="secondary"
            />
            <DashBtn
              label="Operating Expenses"
              onPress={() => navigation.navigate('AdminExpenses')}
              kind="secondary"
            />
            <DashBtn
              label="Tax Compliance"
              onPress={() => navigation.navigate('AdminTaxCompliance')}
              kind="secondary"
            />
          </View>
        </Section>

        <Section title="Key Metrics">
          {kpis.loading ? (
            <ActivityIndicator color={COLORS.GOLD} />
          ) : (
            <View style={styles.kpiRow}>
              <KpiCard 
                label="Gross Revenue" 
                value={usd(kpis.grossRevenue)} 
                icon="$" 
              />
              <KpiCard 
                label="Net Revenue (Platform Fee)" 
                value={usd(kpis.netRevenue)} 
                icon="+" 
              />
              <KpiCard 
                label="Total Custodial Holdings"
                value={usd(kpis.activePot)} 
                icon="*" 
              />
              <KpiCard 
                label="Pending Withdrawals" 
                value={num(kpis.pendingWithdrawals)} 
                icon="!" 
                isAlert={kpis.pendingWithdrawals > 0} 
              />
              <KpiCard 
                label="Live Events" 
                value={num(kpis.liveEvents)} 
                icon="o" 
              />
              <KpiCard 
                label="Total Users" 
                value={num(kpis.totalUsers)} 
                icon="#" 
              />
            </View>
          )}
        </Section>

      </ScrollView>
      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}


// --- UI Components ---
const Section = ({ title, children }) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>{title}</Text>
    {children}
  </View>
);

const KpiCard = ({ label, value, icon, isAlert }) => (
  <View style={styles.kpiBox}>
    <Text style={styles.kpiLabel}>{icon} {label}</Text>
    <Text style={[styles.kpiValue, isAlert && { color: COLORS.DANGER }]}>
      {value}
    </Text>
  </View>
);

const DashBtn = ({ label, onPress, kind, disabled }) => (
  <TouchableOpacity 
    onPress={onPress} 
    disabled={disabled} 
    style={[
      styles.dashBtn, 
      kind === 'primary' ? styles.dashBtnPrimary : styles.dashBtnSecondary, 
      disabled && { opacity: 0.5 }
    ]}
  >
    <Text style={kind === 'primary' ? styles.dashBtnTextPrimary : styles.dashBtnTextSecondary}>
      {label}
    </Text>
  </TouchableOpacity>
);


// --- Styles ---
const styles = StyleSheet.create({
  safeArea: { 
    flex: 1, 
    backgroundColor: COLORS.BG 
  },
  container: { 
    padding: 16, 
    paddingBottom: 120 
  },
  centerWrap: { 
    flex: 1, 
    justifyContent: 'center', 
    alignItems: 'center', 
    padding: 20 
  },
  deniedText: { 
    color: COLORS.DANGER, 
    fontSize: 18, 
    fontWeight: 'bold', 
    textAlign: 'center' 
  },
  backLink: { 
    color: COLORS.DIM, 
    marginTop: 16, 
    textDecorationLine: 'underline' 
  },
  header: { 
    alignItems: 'center', 
    marginBottom: 16 
  },
  title: { 
    fontSize: 28, 
    fontWeight: '900', 
    color: COLORS.GOLD, 
    textAlign: 'center' 
  },
  subtitle: { 
    color: COLORS.DIM, 
    fontSize: 14, 
    marginTop: 4, 
    textAlign: 'center' 
  },
  
  // Stripe Status Banner
  stripeBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 10,
    marginBottom: 16,
    borderWidth: 1,
  },
  stripeBannerDisabled: {
    backgroundColor: '#1a1a1a',
    borderColor: '#333',
  },
  stripeBannerTest: {
    backgroundColor: '#1a2a1a',
    borderColor: '#4CAF50',
  },
  stripeBannerLive: {
    backgroundColor: '#2a1a1a',
    borderColor: '#E74C3C',
  },
  stripeBannerIcon: {
    fontSize: 14,
    marginRight: 12,
    color: '#fff',
    fontWeight: '700',
  },
  stripeBannerText: {
    flex: 1,
  },
  stripeBannerTitle: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  stripeBannerSub: {
    color: '#888',
    fontSize: 12,
    marginTop: 2,
  },
  stripeBannerArrow: {
    color: '#666',
    fontSize: 18,
  },
  
  // Event Creation Toggle
  eventToggleBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 10,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    backgroundColor: COLORS.CARD,
  },
  eventToggleText: {
    flex: 1,
  },
  eventToggleTitle: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  eventToggleSub: {
    color: '#888',
    fontSize: 12,
    marginTop: 2,
  },

  seasonStatus: {
    color: COLORS.GOLD,
    fontSize: 15,
    fontWeight: '800',
  },
  seasonSub: {
    color: COLORS.DIM,
    fontSize: 13,
    marginTop: 4,
    lineHeight: 18,
  },

  section: {
    width: '100%',
    marginBottom: 24
  },
  sectionTitle: { 
    color: COLORS.TEXT, 
    fontSize: 16, 
    fontWeight: 'bold', 
    marginBottom: 12, 
    borderLeftWidth: 3, 
    borderLeftColor: COLORS.GOLD, 
    paddingLeft: 8 
  },
  kpiRow: { 
    flexDirection: 'row', 
    flexWrap: 'wrap', 
    margin: -6 
  },
  kpiBox: { 
    flex: 1, 
    minWidth: '45%', 
    margin: 6, 
    backgroundColor: COLORS.CARD, 
    borderRadius: 8, 
    borderWidth: 1, 
    borderColor: COLORS.BORDER, 
    padding: 14 
  },
  kpiLabel: { 
    color: COLORS.DIM, 
    fontSize: 12, 
    fontWeight: '600', 
    marginBottom: 6 
  },
  kpiValue: { 
    color: COLORS.TEXT, 
    fontSize: 24, 
    fontWeight: 'bold' 
  },
  
  quickGrid: { 
    flexDirection: 'row', 
    flexWrap: 'wrap', 
    gap: 12 
  },
  dashBtn: { 
    flex: 1, 
    minWidth: '45%', 
    paddingVertical: 14, 
    paddingHorizontal: 18, 
    borderRadius: 8, 
    borderWidth: 1, 
    alignItems: 'center' 
  },
  dashBtnPrimary: { 
    backgroundColor: COLORS.GOLD, 
    borderColor: COLORS.GOLD 
  },
  dashBtnSecondary: { 
    backgroundColor: 'transparent', 
    borderColor: COLORS.BORDER 
  },
  dashBtnTextPrimary: { 
    color: '#000', 
    fontSize: 14, 
    fontWeight: '700' 
  },
  dashBtnTextSecondary: { 
    color: COLORS.TEXT, 
    fontSize: 14, 
    fontWeight: '700' 
  },
});